import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import BTTable from "react-bootstrap/Table";
import { useTheme } from "styled-components";
import {
  flexRender,
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Search } from "lucide-react";

import { numberSeparator } from "utils";
import { Button } from "components/Button";
import { Checkbox } from "components/Checkbox";
import { PAGE_SIZE } from "utils/constants";
import {
  IconWrapper,
  StyledArrowDown,
  StyledArrowUp,
  StyledArrowUpDown,
  TableContainer,
} from "./Table.styles";
import { TableHeader } from "style/v2/components/TableHeader.styles";
import { TablePlaceholder } from "./TablePlaceholder";
import { IconArrowRight3, IconArrowLeft2 } from "assets/icons";
import {
  // IconSortDown,
  // IconSortUp,
  LeftArrowIcon2,
  RighttArrowIcon2,
} from "assets/icons/index";

const columnHelper = createColumnHelper();

export const Table = ({
  data = [],
  columns = [],
  isLoading = false,
  showEntriesText = true,
  isSubLoading = false,
  notFoundText = "No Data found",
  notFoundComponent,
  initialPageSize = PAGE_SIZE,
  headerClass,
  showPagination = true,
  onRowClick = () => {},
  tableClassName = "",
  totalRecords = 0,
  customPagination = false,
  handlePageChange,
  totalPages = 0,
  renderSubComponent,
  getRowCanExpand = () => false,
  showHeader = true,
  onSortChange = () => {},
  sorting = [],
  showPaginationAtTop = false,
  currentPage: externalCurrentPage, // eslint-disable-line no-unused-vars
  mobileHiddenColumns = [], // Array of column dataFields to hide on mobile
  responsiveBreakpoint = 768, // Breakpoint for mobile responsive behavior
  enableMobileStacking = false,
  showSearch = false,
  searchValue,
  onSearchChange,
  getRowId,
  getRowCanSelect,
  customSorting = false,
  enableBulkSelect = false,
  onSelectionChange = () => {},
  tableRef: externalTableRef,
  ...props
}) => {
  const theme = useTheme();
  const tableRef = useRef(null);
  const tableInstanceRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  // Hook to detect mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= responsiveBreakpoint);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, [responsiveBreakpoint]);

  // Filter columns based on mobile visibility
  const visibleColumns = isMobile
    ? columns.filter(
        (column) => !mobileHiddenColumns.includes(column.dataField)
      )
    : columns;

  const tableColumns = useMemo(() => {
    const baseColumns = visibleColumns.map((column) => {
      return columnHelper.accessor(column.dataField, {
        header: () => column.text ?? column.dataField,
        ...column.props,
      });
    });

    if (!enableBulkSelect) {
      return baseColumns;
    }

    const selectionColumn = columnHelper.display({
      id: "select",
      header: ({ table }) => {
        const isAllSelected = table.getIsAllRowsSelected();
        const isSomeSelected = table.getIsSomeRowsSelected();

        return (
          <Checkbox
            checked={isAllSelected}
            indeterminate={!isAllSelected && isSomeSelected}
            onChange={table.getToggleAllRowsSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            className="table-bulk-select-checkbox"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          className={classNames("table-bulk-select-checkbox", {
            "table-bulk-select-checkbox--disabled": !row.getCanSelect(),
          })}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      size: 50,
      minSize: 50,
      maxSize: 50,
    });

    return [selectionColumn, ...baseColumns];
  }, [visibleColumns, enableBulkSelect, columnHelper]);

  const pageCount = customPagination
    ? totalPages
    : Math.ceil(data.length / initialPageSize);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getRowId,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: customPagination,
    manualSorting: customSorting,
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
    pageCount,
    state: {
      sorting,
      pageSize: initialPageSize,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: initialPageSize,
      },
    },
    onSortingChange: (newSorting) => {
      onSortChange(newSorting);
      table.setPageIndex(0);
    },
    autoResetPageIndex: false,
    enableRowSelection: enableBulkSelect
      ? (row) =>
          typeof getRowCanSelect === "function"
            ? getRowCanSelect(row.original)
            : true
      : false,
    onRowSelectionChange: setRowSelection,
  });

  // Store table instance in ref for use in effects
  tableInstanceRef.current = table;

  // Calculate columns for search row colSpan
  const totalColumns = table.getHeaderGroups()?.[0]?.headers?.length ?? 0;
  const usedColumns = enableBulkSelect ? 2 : 1;
  const remainingColumns = Math.max(1, totalColumns - usedColumns);

  // Expose table instance to parent via ref
  useEffect(() => {
    if (externalTableRef) {
      if (typeof externalTableRef === "function") {
        externalTableRef(table);
      } else {
        externalTableRef.current = table;
      }
    }
  }, [table, externalTableRef]);

  useEffect(() => {
    if (!enableBulkSelect) return;
    if (!tableInstanceRef.current) return;

    const selectedRows = tableInstanceRef.current
      .getSelectedRowModel()
      .flatRows.map((row) => row.original);

    onSelectionChange(selectedRows);
  }, [rowSelection, enableBulkSelect, onSelectionChange]);

  const currentPage = table.getState().pagination.pageIndex + 1;
  const pageSize = table.getState().pagination.pageSize;
  const showEntries = (currentPage - 1) * pageSize + 1;
  const totalData = customPagination ? totalRecords : data?.length;
  const toEntriesCalculation = showEntries + pageSize - 1;
  const toEntries =
    toEntriesCalculation > totalData ? totalData : toEntriesCalculation;

  useEffect(() => {
    table.setPageSize(initialPageSize);
  }, [initialPageSize]);

  useEffect(() => {
    table.setPageIndex(0);
  }, [totalPages]);

  const onPageChange = (page) => {
    const pageIndex = page - 1;

    if (customPagination) {
      table.setPageIndex(pageIndex);
      handlePageChange(page);
    } else {
      const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 5;

      if (isBottom && pageSize <= totalData) {
        table.setPageIndex(pageIndex);
        table.setPageSize(pageSize + initialPageSize);
      }
    }
  };

  const handleScroll = useCallback(() => {
    if (tableRef.current && !isLoading && !isSubLoading) {
      const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 5;

      if (isBottom && currentPage < pageCount) {
        onPageChange(currentPage + 1);
      }
    }
  }, [customPagination, currentPage, pageCount, onPageChange]);

  useEffect(() => {
    const container = tableRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  const handleMoreClick = () => {
    if (currentPage + 5 <= pageCount) onPageChange(currentPage + 5);
    else onPageChange(pageCount);
  };

  const getPageButtons = () => {
    return (
      <>
        {Array(pageCount)
          .fill(0)
          .map((d, inx) => {
            const pageNo = inx + 1;
            return (
              <Fragment key={inx}>
                {((currentPage <= 5 && pageNo <= 5) ||
                  (currentPage > 5 &&
                    pageNo > currentPage - 5 &&
                    pageNo <= currentPage)) && (
                  <Button
                    label={pageNo.toString()}
                    size="small"
                    width={40}
                    className={classNames("rounded-0", {
                      "inactive-page": currentPage !== pageNo,
                      "active-page-strategy": currentPage == pageNo,
                    })}
                    key={inx}
                    onClick={() => onPageChange(pageNo)}
                  />
                )}
              </Fragment>
            );
          })}
        {table.getCanNextPage() && (
          <Button
            label="..."
            size="small"
            width={40}
            className="rounded-0 inactive-page"
            onClick={handleMoreClick}
          />
        )}
      </>
    );
  };

  return (
    <TableContainer
      className={classNames("table-container", {
        "mobile-responsive": isMobile,
        "mobile-stacking": isMobile && enableMobileStacking,
      })}
    >
      {showPagination && showPaginationAtTop && !isLoading && (
        <>
          <BTTable className="mb-2">
            <tfoot>
              <tr>
                <td
                  colSpan={tableColumns.length}
                  className="top-table-pagination"
                >
                  {!!data.length && (
                    <div className="record-counter-wrapper">
                      <p className="data-count-text">
                        Found {numberSeparator(totalData, 0)}
                      </p>

                      <div className="d-flex align-items-center">
                        <IconArrowLeft2
                          stroke={theme.image.color.quaternary}
                          onClick={() =>
                            customPagination
                              ? onPageChange(currentPage - 1)
                              : table.previousPage()
                          }
                          disabled={!table.getCanPreviousPage()}
                          className="pagination-btn prev"
                        />

                        {getPageButtons()}

                        <IconArrowRight3
                          stroke={theme.image.color.quaternary}
                          onClick={() =>
                            customPagination
                              ? onPageChange(currentPage + 1)
                              : table.nextPage()
                          }
                          disabled={!table.getCanNextPage()}
                          className="pagination-btn next"
                        />
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            </tfoot>
          </BTTable>
          <div className="divider mt-0 mb-0" />
        </>
      )}

      <div className="table-scroll-wrapper" ref={tableRef}>
        <BTTable
          {...props}
          className={["table-wrapper", tableClassName].join(" ")}
        >
          {showHeader && (
            <thead className={headerClass}>
              {table.getHeaderGroups().map((hg, i) => (
                <tr key={`${hg.id}-${i}`} className="table-row">
                  {hg.headers.map((header, j) => {
                    const headerColumn = header.column;

                    return (
                      <TableHeader
                        key={`${header.id}-${j}`}
                        {...{
                          className: classNames("table-header", {
                            "sorting-column":
                              headerColumn.columnDef.enableSorting,
                          }),
                          onClick: headerColumn.getToggleSortingHandler(),
                        }}
                      >
                        <div className="header-content">
                          {flexRender(
                            headerColumn.columnDef.header,
                            header.getContext()
                          )}
                          {headerColumn.columnDef.enableSorting && (
                            <IconWrapper>
                              {{
                                asc: <StyledArrowUp />,
                                desc: <StyledArrowDown />,
                              }[headerColumn.getIsSorted()] ?? (
                                <StyledArrowUpDown />
                              )}
                            </IconWrapper>
                          )}
                        </div>
                      </TableHeader>
                    );
                  })}
                </tr>
              ))}
            </thead>
          )}
          <tbody>
            {/* loader */}
            {showSearch && (
              <tr className="search-row">
                {enableBulkSelect && <td></td>}
                <td className="no-search-container">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchValue}
                    onChange={onSearchChange}
                  />
                </td>
                <td colSpan={remainingColumns} />
              </tr>
            )}

            {isLoading && (
              <TablePlaceholder headers={table.getHeaderGroups()[0].headers} />
            )}

            {/* no data */}
            {!data.length && !isLoading && (
              <tr className="no-data-row">
                <td
                  className="no-data-text"
                  colSpan={
                    notFoundComponent
                      ? 12
                      : table.getHeaderGroups()[0].headers.length
                  }
                >
                  {notFoundComponent ? notFoundComponent() : notFoundText}
                </td>
              </tr>
            )}

            {/* content */}
            {data.length > 0 &&
              !isLoading &&
              table.getRowModel().rows.map((row, i) => {
                return (
                  <Fragment key={`${row.id}-${i}`}>
                    <tr
                      className={classNames({
                        "active-row":
                          row.original.broker_account_id ===
                          row.original.selectedRow,
                        "ineligible-row": !row.original.eligible,
                        "deleting-row": row.original.isDeleting,
                      })}
                    >
                      {row.getVisibleCells().map((cell, j) => {
                        // Find the original column definition for data-label
                        const originalColumn = visibleColumns.find(
                          (col) => col.dataField === cell.column.id
                        );
                        const dataLabel =
                          originalColumn?.text ||
                          originalColumn?.dataField ||
                          cell.column.id;

                        return (
                          <td
                            style={{
                              maxWidth: cell.column.columnDef.maxWidth,
                              minWidth: cell.column.columnDef.minWidth,
                            }}
                            onClick={() => {
                              if (
                                cell.column.id !== "action" &&
                                cell.column.id !== "select"
                              ) {
                                onRowClick(cell.row.original);
                              }
                            }}
                            className={classNames(
                              "table-cell",
                              cell.column.columnDef?.className
                            )}
                            data-label={
                              isMobile && enableMobileStacking
                                ? dataLabel
                                : undefined
                            }
                            key={`${cell.id}-${j}`}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {row.getIsExpanded() && (
                      <tr>
                        <td colSpan={row.getVisibleCells().length}>
                          {renderSubComponent({ row })}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}

            {/* sub loader */}
            {isSubLoading && (
              <TablePlaceholder headers={table.getHeaderGroups()[0].headers} />
            )}
          </tbody>
          {showPagination && !showPaginationAtTop && !isLoading && (
            <tfoot>
              <tr>
                <td colSpan={tableColumns.length} className="table-pagination">
                  {!!data.length && !isLoading && (
                    <div className="record-counter-wrapper">
                      {showEntriesText && (
                        <p className="data-count-text">
                          Showing {showEntries} to {toEntries} of {totalData}{" "}
                          entries
                        </p>
                      )}

                      <div className="page-button-wrapper">
                        <div className="button-container">
                          <Button
                            label={
                              !showEntriesText && (
                                <>
                                  <img src={LeftArrowIcon2} alt="right arrow" />{" "}
                                  Previous
                                </>
                              )
                            }
                            onClick={() =>
                              customPagination
                                ? onPageChange(currentPage - 1)
                                : table.previousPage()
                            }
                            disabled={!table.getCanPreviousPage()}
                            size="small"
                            width={90}
                            className="pagination-btn prev"
                          />
                        </div>

                        {getPageButtons()}

                        <div className="button-container">
                          <Button
                            label={
                              !showEntriesText && (
                                <>
                                  Next{" "}
                                  <img
                                    src={RighttArrowIcon2}
                                    alt="right arrow"
                                  />
                                </>
                              )
                            }
                            onClick={() =>
                              customPagination
                                ? onPageChange(currentPage + 1)
                                : table.nextPage()
                            }
                            disabled={!table.getCanNextPage()}
                            size="small"
                            width={60}
                            className="pagination-btn next"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            </tfoot>
          )}
        </BTTable>
      </div>
    </TableContainer>
  );
};

Table.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  showEntriesText: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSubLoading: PropTypes.bool,
  notFoundText: PropTypes.string,
  notFoundComponent: PropTypes.func,
  initialPageSize: PropTypes.number,
  headerClass: PropTypes.string,
  showPagination: PropTypes.bool,
  onRowClick: PropTypes.func,
  tableClassName: PropTypes.string,
  totalRecords: PropTypes.number,
  customPagination: PropTypes.bool,
  handlePageChange: PropTypes.func,
  totalPages: PropTypes.number,
  getRowCanExpand: PropTypes.func,
  renderSubComponent: PropTypes.func,
  showHeader: PropTypes.bool,
  customSorting: PropTypes.bool,
  onSortChange: PropTypes.func,
  sorting: PropTypes.array,
  showPaginationAtTop: PropTypes.bool,
  currentPage: PropTypes.number,
  mobileHiddenColumns: PropTypes.array,
  responsiveBreakpoint: PropTypes.number,
  enableMobileStacking: PropTypes.bool,
  showSearch: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  getRowId: PropTypes.func,
  getRowCanSelect: PropTypes.func,
  enableBulkSelect: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  tableRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
